import { useEffect, useMemo, useState } from 'react';
import {
  EVIDENCE_METRICS,
  EVIDENCE_METRIC_LABELS,
  EVIDENCE_QUALITIES,
  EVIDENCE_QUALITY_LABELS,
  type EvidenceMetric,
  type EvidenceQuality,
} from '@ave/shared';
import { api } from '../lib/api';
import { Icon } from './icons';

/**
 * Keying an assumption, with the evidence it rests on.
 *
 * A discount can be typed as a number anywhere in this system and the
 * arithmetic will work perfectly. What it will not survive is a Shares &
 * Assets Valuation officer asking where 35% came from — and by the time that
 * question arrives, the person who chose it has moved on and the reasoning
 * is in nobody's head.
 *
 * So the form does not accept a value on its own. It asks for the
 * observations behind it, how the point within them was chosen, and whether
 * the range was settled before the point was picked. That last one is not
 * bureaucracy: a range assembled after the answer is known is a
 * rationalisation, and it reads identically to a real one on the page.
 *
 * The corridor verdict is shown while typing rather than after saving,
 * because "this will raise a blocking exception a reviewer must clear" is
 * something to know before committing to the number, not after.
 */

const ASSUMPTION_KEYS: { key: string; label: string; metric: EvidenceMetric; unit: 'percent' | 'ratio' }[] = [
  { key: 'discounts.dlom.unquoted', label: 'DLOM — unquoted shares', metric: 'dlom_observation', unit: 'percent' },
  { key: 'discounts.dloc.small_minority', label: 'DLOC — small minority', metric: 'dloc_observation', unit: 'percent' },
  { key: 'discounts.dloc.influential_minority', label: 'DLOC — influential minority', metric: 'dloc_observation', unit: 'percent' },
  { key: 'discounts.dloc.control', label: 'DLOC — control holding', metric: 'dloc_observation', unit: 'percent' },
  { key: 'earnings.ebitda_multiple', label: 'EBITDA multiple', metric: 'ebitda_multiple', unit: 'ratio' },
  { key: 'dcf.discount_rate', label: 'DCF — discount rate', metric: 'other', unit: 'percent' },
  { key: 'dcf.terminal_growth', label: 'DCF — terminal growth', metric: 'other', unit: 'percent' },
  { key: 'dcf.small_company_risk_premium', label: 'DCF — small company risk premium', metric: 'other', unit: 'percent' },
  { key: 'latent_tax.deduction_pct', label: 'Latent tax — deduction', metric: 'other', unit: 'percent' },
  { key: 'instruments.volatility', label: 'Instruments — volatility', metric: 'volatility', unit: 'percent' },
];

interface EvidenceDraft {
  metric: EvidenceMetric;
  value: string;
  asAt: string;
  sourceName: string;
  sourceUrl: string;
  quality: EvidenceQuality;
  notes: string;
}

function blankEvidence(metric: EvidenceMetric, asAt: string): EvidenceDraft {
  return {
    metric,
    value: '',
    asAt,
    sourceName: '',
    sourceUrl: '',
    quality: 'published_study',
    notes: '',
  };
}

/** Percent in the box, ratio on the wire — the engine works in ratios. */
function toRatio(entered: string, unit: 'percent' | 'ratio'): string | null {
  if (!/^-?\d+(\.\d+)?$/.test(entered.trim())) return null;
  const n = Number(entered);
  return unit === 'percent' ? String(n / 100) : String(n);
}

function fromRatio(ratio: number, unit: 'percent' | 'ratio'): string {
  const n = unit === 'percent' ? ratio * 100 : ratio;
  return `${Number(n.toFixed(4))}${unit === 'percent' ? '%' : '×'}`;
}

export function RecordAssumption({
  engagementId,
  valuationDate,
  initialKey,
  onClose,
  onSaved,
}: {
  engagementId: string;
  valuationDate: string;
  initialKey?: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [keyName, setKeyName] = useState(initialKey ?? ASSUMPTION_KEYS[0]!.key);
  const spec = useMemo(
    () => ASSUMPTION_KEYS.find((k) => k.key === keyName) ?? ASSUMPTION_KEYS[0]!,
    [keyName],
  );

  const [entered, setEntered] = useState('');
  const [derivation, setDerivation] = useState('');
  const [justification, setJustification] = useState('');
  const [rangeBeforePoint, setRangeBeforePoint] = useState(false);
  const [comparablesAssessed, setComparablesAssessed] = useState(false);
  const [evidence, setEvidence] = useState<EvidenceDraft[]>([
    blankEvidence(spec.metric, valuationDate),
  ]);
  const [bounds, setBounds] = useState<{ min: number | null; max: number | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The corridor in force at the valuation date, not at today's date: a
  // valuation dated 2019 is checked against the 2019 band.
  useEffect(() => {
    let live = true;
    setBounds(null);
    api
      .assumptionCorridor(engagementId, keyName)
      .then((r) => live && setBounds(r.bounds))
      .catch(() => live && setBounds(null));
    setEvidence((rows) =>
      rows.map((r, i) => (i === 0 && !r.sourceName ? blankEvidence(spec.metric, valuationDate) : r)),
    );
    return () => {
      live = false;
    };
  }, [engagementId, keyName, spec.metric, valuationDate]);

  const ratio = toRatio(entered, spec.unit);
  const outside =
    ratio !== null &&
    bounds !== null &&
    ((bounds.min !== null && Number(ratio) < bounds.min) ||
      (bounds.max !== null && Number(ratio) > bounds.max));

  const filledEvidence = evidence.filter((e) => e.sourceName.trim() && e.value.trim());
  const valid =
    ratio !== null &&
    derivation.trim().length >= 10 &&
    filledEvidence.length > 0 &&
    (!outside || justification.trim().length > 0);

  const submit = async () => {
    if (ratio === null) return;
    setBusy(true);
    setError(null);
    try {
      await api.recordAssumption(engagementId, {
        assumptionKey: keyName,
        value: ratio,
        unit: spec.unit,
        derivation: derivation.trim(),
        evidence: filledEvidence.map((e) => ({
          metric: e.metric,
          value: toRatio(e.value, spec.unit) ?? e.value,
          asAt: e.asAt,
          sourceName: e.sourceName.trim(),
          sourceUrl: e.sourceUrl.trim() || undefined,
          quality: e.quality,
          notes: e.notes.trim() || undefined,
        })),
        disciplines: { rangeBeforePoint, comparablesAssessed },
        justification: justification.trim() || undefined,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the assumption');
      setBusy(false);
    }
  };

  const setRow = (i: number, patch: Partial<EvidenceDraft>) =>
    setEvidence((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-10 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card animate-fade-up w-full max-w-3xl overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-teal-500 to-teal-600" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Key an assumption, with its evidence
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              The number and what stands behind it are recorded together. A figure whose derivation
              is in somebody&rsquo;s head is a figure nobody can defend.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <Icon name="x" className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="label">Assumption</span>
              <select
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                {ASSUMPTION_KEYS.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </select>
              <span className="mt-1 block font-mono text-xs text-slate-400">{keyName}</span>
            </label>

            <label className="block">
              <span className="label">
                Value adopted {spec.unit === 'percent' ? '(%)' : '(×)'}
              </span>
              <input
                autoFocus
                inputMode="decimal"
                value={entered}
                onChange={(e) => setEntered(e.target.value)}
                placeholder={spec.unit === 'percent' ? '35' : '6.5'}
                className="tnum mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>
          </div>

          {/* What recording this will do, before it is recorded. */}
          <CorridorVerdict
            bounds={bounds}
            ratio={ratio}
            unit={spec.unit}
            outside={outside}
            valuationDate={valuationDate}
          />

          <label className="block">
            <span className="label">How the value was arrived at</span>
            <textarea
              value={derivation}
              onChange={(e) => setDerivation(e.target.value)}
              rows={2}
              placeholder="e.g. Midpoint of the FMV restricted-stock range, shaded down for the company's dividend record and the absence of a pre-emption queue"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
            <span className="mt-1 block text-xs text-slate-400">
              The reasoning a reviewer, and later an inspector, will read. Not the arithmetic —
              where within the range, and why there.
            </span>
          </label>

          <div>
            <div className="flex items-baseline justify-between">
              <span className="label">The observations behind it</span>
              <button
                type="button"
                onClick={() => setEvidence((r) => [...r, blankEvidence(spec.metric, valuationDate)])}
                className="text-xs font-medium text-teal-700 hover:text-teal-900"
              >
                + another observation
              </button>
            </div>
            <div className="mt-2 space-y-3">
              {evidence.map((row, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="grid gap-3 sm:grid-cols-4">
                    <label className="block sm:col-span-2">
                      <span className="text-xs text-slate-500">Measures</span>
                      <select
                        value={row.metric}
                        onChange={(e) => setRow(i, { metric: e.target.value as EvidenceMetric })}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                      >
                        {EVIDENCE_METRICS.map((m) => (
                          <option key={m} value={m}>
                            {EVIDENCE_METRIC_LABELS[m]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs text-slate-500">
                        Observed {spec.unit === 'percent' ? '(%)' : '(×)'}
                      </span>
                      <input
                        inputMode="decimal"
                        value={row.value}
                        onChange={(e) => setRow(i, { value: e.target.value })}
                        className="tnum mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-slate-500">As at</span>
                      <input
                        type="date"
                        value={row.asAt}
                        onChange={(e) => setRow(i, { asAt: e.target.value })}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs text-slate-500">Source</span>
                      <input
                        value={row.sourceName}
                        onChange={(e) => setRow(i, { sourceName: e.target.value })}
                        placeholder="e.g. FMV Opinions Restricted Stock Study 2024"
                        className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs text-slate-500">Weight</span>
                      <select
                        value={row.quality}
                        onChange={(e) => setRow(i, { quality: e.target.value as EvidenceQuality })}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                      >
                        {EVIDENCE_QUALITIES.map((q) => (
                          <option key={q} value={q}>
                            {EVIDENCE_QUALITY_LABELS[q]}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {row.asAt > valuationDate ? (
                    <p className="mt-2 text-xs text-amber-700">
                      Dated after the valuation date. It will be recorded and marked as hindsight,
                      and it is not evidence of value at {valuationDate}.
                    </p>
                  ) : null}
                  {evidence.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => setEvidence((r) => r.filter((_, j) => j !== i))}
                      className="mt-2 text-xs text-slate-400 hover:text-rose-600"
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          {/* Two questions that decide whether the range means anything. */}
          <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
            <Discipline checked={rangeBeforePoint} onChange={setRangeBeforePoint}>
              The range was settled <strong className="font-medium">before</strong> the point within
              it was chosen
              <span className="mt-0.5 block text-xs text-slate-500">
                A range assembled after the answer is known is a rationalisation, and it reads
                exactly like a real one.
              </span>
            </Discipline>
            <Discipline checked={comparablesAssessed} onChange={setComparablesAssessed}>
              The observations were assessed for comparability in size, sector and marketability
              <span className="mt-0.5 block text-xs text-slate-500">
                A restricted-stock study of US listed companies is evidence about US listed
                companies until someone says why it bears on this one.
              </span>
            </Discipline>
          </div>

          {outside ? (
            <label className="block">
              <span className="label">
                Justification
                <span className="ml-1.5 font-normal normal-case tracking-normal text-amber-700">
                  required — this value leaves its corridor
                </span>
              </span>
              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={2}
                placeholder="Why this engagement warrants a value outside the band a reviewer would otherwise expect"
                className="mt-1.5 w-full rounded-lg border border-amber-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
            </label>
          ) : null}

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-3.5">
          <p className="text-xs text-slate-500">
            {filledEvidence.length === 0
              ? 'At least one observation is needed — a value with no evidence is the thing this form exists to prevent.'
              : `${filledEvidence.length} observation${filledEvidence.length === 1 ? '' : 's'} will be recorded with the value.`}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
              {busy ? 'Recording…' : outside ? 'Record and raise the exception' : 'Record the assumption'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CorridorVerdict({
  bounds,
  ratio,
  unit,
  outside,
  valuationDate,
}: {
  bounds: { min: number | null; max: number | null } | null;
  ratio: string | null;
  unit: 'percent' | 'ratio';
  outside: boolean;
  valuationDate: string;
}) {
  if (!bounds || (bounds.min === null && bounds.max === null)) {
    return (
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">
        No corridor is in force for this assumption at {valuationDate}. The value is recorded with
        its evidence and nothing is raised.
      </p>
    );
  }
  const band = `${bounds.min !== null ? fromRatio(bounds.min, unit) : '—'} to ${
    bounds.max !== null ? fromRatio(bounds.max, unit) : '—'
  }`;
  if (ratio === null) {
    return (
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
        The corridor in force at {valuationDate} is <strong className="font-medium">{band}</strong>.
      </p>
    );
  }
  return outside ? (
    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <Icon name="alert" className="mr-1 inline h-4 w-4" />
      {fromRatio(Number(ratio), unit)} is outside the corridor of {band} in force at {valuationDate}.
      Recording it raises an exception that blocks the stage gate until a reviewer approves it, and
      a material one renders in the report. The judgement is allowed to leave the corridor; it
      cannot do so silently.
    </p>
  ) : (
    <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
      <Icon name="check" className="mr-1 inline h-4 w-4" strokeWidth={2.5} />
      {fromRatio(Number(ratio), unit)} sits inside the corridor of {band} in force at{' '}
      {valuationDate}. Nothing is raised.
    </p>
  );
}

function Discipline({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
      />
      <span>{children}</span>
    </label>
  );
}
